import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import { Close, FileOutlinedIcon, SearchRoundedIcon } from "../../utilities/icons";
import { getUsers } from "../../apiCalls/Apicalls";
import EmployeeDocuments from "./EmployeeDocuments";

const BLUE = "#418BFF";
const BLUE_OVERLAY = "rgba(65,139,255,0.12)";

const CATEGORY_ORDER = ["resume", "photo", "id_proof", "certificate", "other"];
const CATEGORY_LABELS = {
  resume: "Resume",
  photo: "Photo",
  id_proof: "ID Proof",
  certificate: "Certificate",
  other: "Other",
};
const CATEGORY_COLORS = {
  resume: { bg: "rgba(65,139,255,0.1)", color: "#418BFF" },
  photo: { bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
  id_proof: { bg: "rgba(251,146,60,0.1)", color: "#ea580c" },
  certificate: { bg: "rgba(168,85,247,0.1)", color: "#9333ea" },
  other: { bg: "rgba(100,116,139,0.1)", color: "#475569" },
};

function getInitials(user) {
  return `${user?.FirstName?.[0] || ""}${user?.LastName?.[0] || ""}`.toUpperCase();
}

function DocCountBadges({ counts }) {
  if (!counts || Object.keys(counts).length === 0) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {CATEGORY_ORDER.filter((c) => counts[c]).map((cat) => {
        const s = CATEGORY_COLORS[cat];
        return (
          <Box
            key={cat}
            sx={{
              px: 0.75,
              py: 0.15,
              bgcolor: s.bg,
              color: s.color,
              borderRadius: "4px",
              fontSize: "0.68rem",
              fontWeight: 600,
              lineHeight: 1.7,
            }}
          >
            {CATEGORY_LABELS[cat]} {counts[cat]}
          </Box>
        );
      })}
    </Stack>
  );
}

function EmployeeCard({ user, docCounts, onClick }) {
  const fullName =
    user.FullName ||
    `${user.FirstName || ""} ${user.LastName || ""}`.trim() ||
    user.Email;
  const total = Object.values(docCounts || {}).reduce((s, n) => s + n, 0);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: "1px solid var(--ax-border)",
        cursor: "pointer",
        "&:hover": { bgcolor: "var(--ax-bg)" },
        "&:last-child": { borderBottom: "none" },
        transition: "background 120ms ease",
      }}
    >
      <Avatar
        sx={{
          width: 38,
          height: 38,
          bgcolor: BLUE_OVERLAY,
          color: BLUE,
          fontWeight: 600,
          fontSize: 13,
          borderRadius: "9px",
          flexShrink: 0,
        }}
      >
        {getInitials(user)}
      </Avatar>

      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.88rem",
              color: "var(--ax-ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fullName}
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", color: "var(--ax-ink-2)", flexShrink: 0 }}
          >
            · {user.EmpId}
          </Typography>
        </Stack>
        <Typography
          sx={{ fontSize: "0.75rem", color: "var(--ax-ink-2)", mb: 0.5 }}
        >
          {user.JobTitle || "—"} &nbsp;·&nbsp; {user.SpaceName || "—"}
        </Typography>
        <DocCountBadges counts={docCounts} />
      </Box>

      <Box flexShrink={0}>
        <Chip
          label={total ? `${total} file${total !== 1 ? "s" : ""}` : "No files"}
          size="small"
          sx={{
            bgcolor: total ? BLUE_OVERLAY : "var(--ax-bg)",
            color: total ? BLUE : "var(--ax-ink-2)",
            fontWeight: 600,
            fontSize: "0.72rem",
            borderRadius: "6px",
          }}
        />
      </Box>
    </Stack>
  );
}

function DocumentsPage() {
  const [users, setUsers] = useState([]);
  const [docCounts, setDocCounts] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: Cookies.get("jwtToken") } });

  const fetchUsers = useCallback(() => {
    setLoadingUsers(true);
    axios
      .get(getUsers, authHeaders())
      .then((res) => {
        const list = res.data || [];
        setUsers(list);
        // Fetch document counts per employee in bulk via individual /count calls
        // We use Promise.allSettled so one failure doesn't break others
        return Promise.allSettled(
          list.map((u) =>
            axios
              .get(`${process.env.REACT_APP_HOST}/employees/${u.EmpId}/documents/count`, authHeaders())
              .then((r) => ({ empId: u.EmpId, counts: r.data?.byCategory || {} }))
          )
        );
      })
      .then((results) => {
        const map = {};
        results.forEach((r) => {
          if (r.status === "fulfilled") {
            map[r.value.empId] = r.value.counts;
          }
        });
        setDocCounts(map);
      })
      .catch(() => toast.error("Failed to load employees"))
      .finally(() => setLoadingUsers(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      u.EmpId?.toLowerCase().includes(q) ||
      u.FullName?.toLowerCase().includes(q) ||
      u.FirstName?.toLowerCase().includes(q) ||
      u.LastName?.toLowerCase().includes(q) ||
      u.Email?.toLowerCase().includes(q) ||
      u.SpaceName?.toLowerCase().includes(q)
    );
  });

  const openDrawer = (user) => {
    setSelectedEmployee(user);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Refresh counts after drawer closes (user may have uploaded/deleted)
    fetchUsers();
  };

  const totalFiles = Object.values(docCounts).reduce(
    (s, c) => s + Object.values(c).reduce((a, b) => a + b, 0),
    0
  );
  const employeesWithDocs = Object.values(docCounts).filter(
    (c) => Object.values(c).some((v) => v > 0)
  ).length;

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Summary stats */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        {[
          { label: "Total Employees", value: users.length, color: BLUE },
          { label: "With Documents", value: employeesWithDocs, color: "#16a34a" },
          { label: "Total Files", value: totalFiles, color: "#9333ea" },
        ].map((s) => (
          <Box
            key={s.label}
            sx={{
              flex: 1,
              bgcolor: "var(--ax-surface)",
              border: "1px solid var(--ax-border)",
              borderRadius: "var(--ax-radius-lg)",
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "var(--ax-ink-2)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {s.label}
            </Typography>
            <Typography
              sx={{ fontSize: "1.6rem", fontWeight: 700, color: s.color, mt: 0.5 }}
            >
              {loadingUsers ? <CircularProgress size={20} sx={{ color: s.color }} /> : s.value}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Employee list */}
      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          px={2.5}
          py={2}
          borderBottom="1px solid var(--ax-border)"
          gap={1.5}
        >
          <Typography
            sx={{
              fontFamily: "var(--ax-font-heading)",
              fontWeight: 500,
              fontSize: "1rem",
              color: "var(--ax-ink)",
            }}
          >
            All Employees
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchRoundedIcon fontSize="small" sx={{ color: "var(--ax-ink-2)" }} />
            <TextField
              size="small"
              placeholder="Search by name, ID, department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 280 }}
              inputProps={{ "aria-label": "Search employees" }}
            />
          </Stack>
        </Stack>

        {loadingUsers ? (
          <Stack alignItems="center" py={8}>
            <CircularProgress size={28} sx={{ color: "var(--ax-blue)" }} />
            <Typography sx={{ mt: 1.5, fontSize: "0.84rem", color: "var(--ax-ink-2)" }}>
              Loading employees…
            </Typography>
          </Stack>
        ) : filtered.length === 0 ? (
          <Stack alignItems="center" py={8} spacing={0.75}>
            <FileOutlinedIcon fontSize="large" sx={{ color: "var(--ax-border)" }} />
            <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-ink-2)" }}>
              No employees found
            </Typography>
          </Stack>
        ) : (
          filtered.map((user) => (
            <EmployeeCard
              key={user.EmpId}
              user={user}
              docCounts={docCounts[user.EmpId] || {}}
              onClick={() => openDrawer(user)}
            />
          ))
        )}
      </Box>

      {/* Document drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 520 },
            bgcolor: "var(--ax-bg)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {selectedEmployee && (
          <>
            {/* Drawer header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
              sx={{
                bgcolor: "var(--ax-surface)",
                borderBottom: "1px solid var(--ax-border)",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: BLUE_OVERLAY,
                    color: BLUE,
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: "8px",
                  }}
                >
                  {getInitials(selectedEmployee)}
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      color: "var(--ax-ink)",
                    }}
                  >
                    {selectedEmployee.FullName ||
                      `${selectedEmployee.FirstName || ""} ${selectedEmployee.LastName || ""}`.trim()}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "var(--ax-ink-2)" }}>
                    {selectedEmployee.EmpId} · {selectedEmployee.JobTitle || "—"}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={closeDrawer}
                size="small"
                sx={{ color: "var(--ax-ink-2)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>

            {/* Document manager */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
              <EmployeeDocuments empId={selectedEmployee.EmpId} />
            </Box>
          </>
        )}
      </Drawer>

      <ToastContainer />
    </Box>
  );
}

export default DocumentsPage;
